/**
 * Shared failure handling for problem figures, used by both render paths:
 * <ProblemText> via onErrorCapture, and SurveyRenderer via a DOM listener
 * (SurveyJS injects outside the React tree, so React never sees those errors).
 */

import { S3_MEDIA_BASE } from "./media";

/** Strip the constant bucket prefix, leaving `ka/<hash>.svg`. */
export function compactRef(url: string): string {
  return url.startsWith(S3_MEDIA_BASE)
    ? url.slice(S3_MEDIA_BASE.length).replace(/^\//, "")
    : url;
}

/**
 * Label for a figure we could not show. Alt text describes what the figure
 * was; the URL does not, so prefer it. Shared with ProblemImages so the two
 * render paths cannot drift.
 */
export function missingFigureLabel(alt: string, raw: string): string {
  return alt
    ? `figure not mirrored — ${alt}`
    : `figure not mirrored (${compactRef(raw)})`;
}

/** Replace a failed <img> with a chip, preferring its descriptive alt text. */
function replaceImgWithChip(img: HTMLImageElement): void {
  const src = img.getAttribute("src") ?? "";
  const alt = img.getAttribute("alt")?.trim() ?? "";

  const chip = document.createElement("span");
  chip.className = "qb-chip qb-unresolved";
  chip.title = src;

  const label = document.createElement("span");
  label.textContent = missingFigureLabel(alt, src);
  chip.appendChild(label);

  img.replaceWith(chip);
}

/**
 * Advance a failed <img> to its next candidate URL, or swap in a chip when
 * there are none left. Returns true if a retry was started.
 */
export function advanceOrChip(img: HTMLImageElement): boolean {
  const rest = (img.dataset.qbFallback ?? "").split(" ").filter(Boolean);
  const next = rest.shift();
  if (next) {
    img.dataset.qbFallback = rest.join(" ");
    img.src = next;
    return true;
  }
  replaceImgWithChip(img);
  return false;
}

/**
 * For imperatively-rendered DOM. Capture phase is required: img `error` events
 * do not bubble. Returns a cleanup function.
 */
export function attachImageErrorFallback(root: HTMLElement): () => void {
  const onError = (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target || target.tagName !== "IMG") return;
    advanceOrChip(target as HTMLImageElement);
  };
  root.addEventListener("error", onError, true);
  return () => root.removeEventListener("error", onError, true);
}
